"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// ─── Context ──────────────────────────────────────────────────────────────────

type SwitcherContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedValue: string | null | undefined;
  searchInputRef: RefObject<HTMLInputElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
};

const SwitcherContext = createContext<SwitcherContextValue | null>(null);

function useSwitcherContext() {
  const ctx = useContext(SwitcherContext);
  if (!ctx) {
    throw new Error(
      "Switcher compound components must be used within <Switcher>"
    );
  }
  return ctx;
}

/**
 * Access the switcher context from a child component.
 *
 * Read `searchQuery` to filter items and conditionally render empty states.
 */
function useSwitcher() {
  const ctx = useSwitcherContext();
  return {
    searchQuery: ctx.searchQuery,
    open: ctx.open,
    setOpen: ctx.setOpen,
    selectedValue: ctx.selectedValue,
  };
}

// ─── Switcher (root) ─────────────────────────────────────────────────────────

type SwitcherRootProps = {
  children: ReactNode;
  /** Currently selected item value (for highlighting). */
  value?: string | null;
  /** Controlled open state. When omitted the component manages its own state. */
  open?: boolean;
  /** Called when open state changes. Works with both controlled and uncontrolled modes. */
  onOpenChange?: (open: boolean) => void;
};

function Switcher({
  children,
  value,
  open: controlledOpen,
  onOpenChange,
}: SwitcherRootProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
      if (nextOpen) {
        setSearchQuery("");
      }
    },
    [isControlled, onOpenChange]
  );

  const ctx = useMemo<SwitcherContextValue>(
    () => ({
      open,
      setOpen,
      searchQuery,
      setSearchQuery,
      selectedValue: value,
      searchInputRef,
      contentRef,
    }),
    [open, setOpen, searchQuery, value]
  );

  return (
    <SwitcherContext.Provider value={ctx}>
      <DropdownMenu onOpenChange={setOpen} open={open}>
        {children}
      </DropdownMenu>
    </SwitcherContext.Provider>
  );
}

// ─── SwitcherTrigger ──────────────────────────────────────────────────────────

type SwitcherTriggerProps = {
  children: React.ReactElement;
  className?: string;
};

function SwitcherTrigger({ children, className }: SwitcherTriggerProps) {
  return (
    <DropdownMenuTrigger
      className={className}
      data-slot="switcher"
      render={children}
    />
  );
}

// ─── SwitcherContent ──────────────────────────────────────────────────────────

type SwitcherContentProps = {
  children: ReactNode;
  className?: string;
} & Omit<
  React.ComponentProps<typeof DropdownMenuContent>,
  "children" | "className"
>;

function SwitcherContent({
  children,
  className,
  ...props
}: SwitcherContentProps) {
  const { contentRef } = useSwitcherContext();

  return (
    <DropdownMenuContent
      className={cn(
        "max-h-[var(--available-height)] w-80 overflow-y-auto p-2",
        className
      )}
      data-switcher-content
      ref={contentRef}
      {...props}
    >
      {children}
    </DropdownMenuContent>
  );
}

// ─── SwitcherInput ────────────────────────────────────────────────────────────

type SwitcherInputProps = {
  /** @default "Search…" */
  placeholder?: string;
};

const FOCUS_DELAY = 100;

function SwitcherInput({ placeholder = "Search\u2026" }: SwitcherInputProps) {
  const {
    open,
    searchQuery,
    setSearchQuery,
    setOpen,
    contentRef,
    searchInputRef,
  } = useSwitcherContext();

  useEffect(() => {
    if (open && searchInputRef.current) {
      const timer = setTimeout(
        () => searchInputRef.current?.focus(),
        FOCUS_DELAY
      );
      return () => clearTimeout(timer);
    }
  }, [open, searchInputRef]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const firstItem = () =>
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      contentRef.current?.querySelector(
        "[data-switcher-item]"
      ) as HTMLElement | null;

    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      firstItem()?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      firstItem()?.click();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
    e.stopPropagation();
  }

  return (
    <div className="relative sticky top-0 z-10 border-border/60 border-b bg-popover pb-2">
      <Search className="absolute top-2.5 left-2 h-4 w-4 text-muted-foreground" />
      <Input
        className="h-9 border-0 pl-8 focus-visible:ring-1"
        onChange={(e) => setSearchQuery(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={searchInputRef}
        value={searchQuery}
      />
    </div>
  );
}

// ─── SwitcherItem ─────────────────────────────────────────────────────────────

type SwitcherItemBase = {
  /** Unique value — used for selection highlight. */
  value: string;
  /**
   * Text used for search filtering. When the search query doesn't match this
   * text the item is hidden. Omit to make the item always visible.
   */
  textValue?: string;
  /** Render children directly, or use a render function to access `isSelected`. */
  children: ReactNode | ((props: { isSelected: boolean }) => ReactNode);
  className?: string;
};

type SwitcherItemLinkProps = SwitcherItemBase & {
  /** TanStack Router route — renders a real link inside the menu item. */
  to: string;
  params?: Record<string, string>;
  search?: (prev: Record<string, unknown>) => Record<string, unknown>;
  from?: string;
  onSelect?: never;
};

type SwitcherItemActionProps = SwitcherItemBase & {
  /** Non-navigation action (e.g. open a dialog). */
  onSelect: () => void;
  to?: never;
  params?: never;
  search?: never;
  from?: never;
};

export type SwitcherItemProps = SwitcherItemLinkProps | SwitcherItemActionProps;

function SwitcherItem(props: SwitcherItemProps) {
  const { value, textValue, children, className } = props;

  const { searchQuery, selectedValue, searchInputRef, contentRef } =
    useSwitcherContext();

  const isVisible =
    !searchQuery ||
    textValue === undefined ||
    textValue.toLowerCase().includes(searchQuery.toLowerCase());

  if (!isVisible) {
    return null;
  }

  const isSelected = value === selectedValue;
  const content =
    typeof children === "function" ? children({ isSelected }) : children;

  const itemClassName = cn(
    "group flex cursor-pointer items-center gap-2.5 px-3 py-2.5",
    className
  );

  const sharedKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      const allItems = contentRef.current?.querySelectorAll(
        "[data-switcher-item]"
      );
      if (allItems?.[0] === e.currentTarget) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    }
  };

  if ("to" in props && props.to !== undefined) {
    const { to, params, search, from } = props;
    return (
      <DropdownMenuItem
        className={itemClassName}
        data-switcher-item
        data-value={value}
        onKeyDown={sharedKeyDown}
        render={
          <Link
            to={to}
            {...(from !== undefined ? { from } : {})}
            {...(params !== undefined ? { params } : {})}
            {...(search !== undefined ? { search } : {})}
          />
        }
      >
        {content}
      </DropdownMenuItem>
    );
  }

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const { onSelect } = props as SwitcherItemActionProps;

  return (
    <DropdownMenuItem
      className={itemClassName}
      data-switcher-item
      data-value={value}
      onClick={() => onSelect()}
      onKeyDown={sharedKeyDown}
    >
      {content}
    </DropdownMenuItem>
  );
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export type {
  SwitcherContentProps,
  SwitcherInputProps,
  SwitcherTriggerProps,
  SwitcherRootProps as SwitcherProps,
};
export {
  Switcher,
  SwitcherContent,
  SwitcherInput,
  SwitcherItem,
  SwitcherTrigger,
  useSwitcher,
};
